import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";

import { initiateZimswitchChemaDonation } from "./donationsApi";

const ZIMSWITCH_BASE_URL =
  process.env.REACT_APP_ZIMSWITCH_BASE_URL || "https://eu-test.oppwa.com";
const ZIMSWITCH_BRANDS =
  process.env.REACT_APP_ZIMSWITCH_BRANDS || "PRIVATE_LABEL";

interface ZimswitchPaymentFormProps {
  amount: number | null;
  currency: string;
  email: string;
  streamId: number | null;
  pageType?: string;
  fullName?: string;
  onValidate: () => boolean;
}

/**
 * ZimSwitch Online (OPPWA COPY&PAY) widget slot for the chema donation form.
 * Same COPY&PAY mechanism as EFTPay: on "Pay" we prepare a checkout
 * server-side, then inject the OPPWA paymentWidgets.js script + `paymentWidgets`
 * form directly into the modal. The widget renders the hosted card fields and,
 * on submit, redirects (a real top-level navigation — OPPWA's widget does not
 * honor iframe containment) to our shopperResultUrl with a `resourcePath` that
 * `/chema/zimswitch/result` verifies against the backend. The widget must be
 * re-created per checkoutId, so we tear it down on unmount / amount change.
 */
export default function ZimswitchPaymentForm({
  amount,
  currency,
  email,
  streamId,
  pageType,
  fullName,
  onValidate,
}: ZimswitchPaymentFormProps) {
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [donationId, setDonationId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Inject the OPPWA widget once a checkout is prepared, and tear it down on
  // unmount / checkout change — OPPWA leaves globals behind otherwise.
  useEffect(() => {
    if (!checkoutId || !donationId) return;

    const shopperResultUrl = `${window.location.origin}/chema/zimswitch/result?donation_id=${donationId}&stream_id=${streamId ?? ""}&page_type=${encodeURIComponent(pageType ?? "")}&full_name=${encodeURIComponent(fullName ?? "")}`;

    const script = document.createElement("script");
    script.src = `${ZIMSWITCH_BASE_URL}/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
    script.async = true;

    const form = document.createElement("form");
    form.action = shopperResultUrl;
    form.className = "paymentWidgets";
    form.setAttribute("data-brands", ZIMSWITCH_BRANDS);

    const container = containerRef.current;
    container?.appendChild(form);
    container?.appendChild(script);

    return () => {
      script.remove();
      form.remove();
      delete (window as unknown as Record<string, unknown>).wpwlOptions;
      delete (window as unknown as Record<string, unknown>).wpwl;
    };
  }, [checkoutId, donationId, streamId, pageType, fullName]);

  // Reset the widget if the amount or currency changes after a checkout was
  // prepared — a checkout is bound to both.
  useEffect(() => {
    setCheckoutId(null);
    setDonationId(null);
  }, [amount, currency]);

  const handlePrepare = async () => {
    if (!onValidate() || !amount) return;
    setLoading(true);
    setError(null);
    try {
      const res = await initiateZimswitchChemaDonation({
        amount,
        currency,
        email: email.trim() || undefined,
        stream_id: streamId,
      });
      setDonationId(res.donation_id);
      setCheckoutId(res.checkout_id);
    } catch (e) {
      setError(
        (e as Error).message || "Could not start ZimSwitch payment. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Pay securely with ZimSwitch, Visa or Mastercard. Card details are
        processed by the bank&apos;s hosted form — we never see your card number.
      </Typography>

      {!checkoutId ? (
        <Button
          type="button"
          fullWidth
          variant="contained"
          onClick={handlePrepare}
          disabled={loading || !amount}
          sx={{
            mt: 1,
            borderRadius: "999px",
            backgroundColor: "#d97706",
            "&:hover": { backgroundColor: "#b45309" },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
              Preparing...
            </>
          ) : (
            "Pay with ZimSwitch"
          )}
        </Button>
      ) : (
        <Box
          ref={containerRef}
          sx={{
            mt: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            p: { xs: 1, sm: 1.5 },
          }}
        />
      )}
    </Box>
  );
}
